import { SearchParams } from 'backend/services/types/urApiService.types'
import {
  FilterProp,
  FilterPropForExtendedSubjectTree,
  Filters,
  SearchTag,
} from 'frontend/types'

const getTagsFromArray = (
  queryArray: string[],
  filtersItems: FilterProp[],
  queryKey: string,
) => {
  const key = queryKey === 'language' ? 'label' : 'value'

  const filterArray: FilterProp[] = filtersItems.filter(item =>
    queryArray.includes(item[key]),
  )

  return filterArray.map(item => ({
    label: item.label,
    queryKey: queryKey,
    queryValue: queryArray.find(query => query === item[key]),
  }))
}

const getTagFromValue = (
  query: string,
  filtersItems: FilterProp[],
  queryKey: string,
) => {
  const key = queryKey === 'language' ? 'label' : 'value'
  const filterItem: FilterProp | undefined = filtersItems.find(
    item => item[key] === query,
  )

  if (filterItem) {
    return {
      label: filterItem.label,
      queryKey: queryKey,
      queryValue: query,
    }
  }
  return null
}

const getTagsFromSubjectTreeExtendedArray = (
  queryArray: string[],
  filtersItems: FilterPropForExtendedSubjectTree[],
  queryKey: string,
) => {
  const key = 'value'

  const items = filtersItems.flatMap(item => [
    item.mainSubject,
    ...item.subSubjects,
  ])

  const filterArray: FilterProp[] = items.filter(item =>
    queryArray.includes(item[key]),
  )

  return filterArray.map(item => ({
    label: item.label,
    queryKey: queryKey,
    queryValue: queryArray.find(query => query === item[key]),
  }))
}

const getTagFromSubjectTreeExtendedValue = (
  query: string,
  filtersItems: FilterPropForExtendedSubjectTree[],
  queryKey: string,
) => {
  const key = 'value'

  const items = filtersItems.flatMap(item => [
    item.mainSubject,
    ...item.subSubjects,
  ])

  const filterItem: FilterProp | undefined = items.find(
    item => item[key] === query,
  )

  if (filterItem) {
    return {
      label: filterItem.label,
      queryKey: queryKey,
      queryValue: query,
    }
  }
  return null
}

const adapt = (
  filters: Filters,
  params: SearchParams,
  excludedKeys?: string[],
): SearchTag[] => {
  const searchTags = Object.entries(params)
    .map(([key, value]) => {
      if (key === 'language' && !excludedKeys?.includes(key)) {
        if (Array.isArray(value)) {
          return getTagsFromArray(value, filters.languages, 'language')
        } else {
          return getTagFromValue(value, filters.languages, 'language')
        }
      }
      if (key === 'typical_age_range' && !excludedKeys?.includes(key)) {
        if (Array.isArray(value)) {
          return getTagsFromArray(
            value,
            filters.educationLevels,
            'typical_age_range',
          )
        } else {
          return getTagFromValue(
            value,
            filters.educationLevels,
            'typical_age_range',
          )
        }
      }

      if (key === 'subject_tree' && !excludedKeys?.includes(key)) {
        if (Array.isArray(value)) {
          return getTagsFromSubjectTreeExtendedArray(
            value,
            [...filters.subjectTreeExtended, ...filters.knowledgeAreaExtended],
            'subject_tree',
          )
        } else {
          return getTagFromSubjectTreeExtendedValue(
            value,
            [...filters.subjectTreeExtended, ...filters.knowledgeAreaExtended],
            'subject_tree',
          )
        }
      }

      if (key === 'teacher_resource_type' && !excludedKeys?.includes(key)) {
        if (Array.isArray(value)) {
          return getTagsFromArray(
            value,
            filters.teacherResources,
            'teacher_resource_type',
          )
        } else {
          return getTagFromValue(
            value,
            filters.teacherResources,
            'teacher_resource_type',
          )
        }
      }

      if (
        key === 'is_audio_described' &&
        value &&
        !excludedKeys?.includes(key)
      ) {
        return {
          label: 'Syntolkat',
          queryKey: 'is_audio_described',
          queryValue: null,
        }
      }
      if (key === 'is_sign_language_interpreted' && value) {
        return {
          label: 'Teckenspråkstolkat',
          queryKey: 'is_sign_language_interpreted',
          queryValue: null,
        }
      }
    })
    .flat()

  return searchTags.filter(Boolean) as SearchTag[]
}

const searchTagsAdapter = { adapt }

export default searchTagsAdapter
